import { termSpell, termSpells } from "../../engine/adventure/ActiveAdventure.js";
import React, { useEffect, useRef, useState } from "react";
import { EventBus } from "@engine/EventBus.js";
import { PartyStore } from "@store/PartyStore.js";
import { loadSpell } from "@engine/loaders/SpellLoader.js";
import { gameActor } from "@ui/hooks/useGameMachine.js";
import { getCombatManager } from "@engine/combat/combatManagerRef.js";
import { findCastableSlot } from "@engine/spells/SpellSlots.js";
import type { Spell } from "@engine/types/spells.js";

/**
 * S21 — SpellChooser modal.
 *
 * Opens on `ui:open_spell_chooser` (S20 routes hand taps with a spellbook
 * or holy-symbol equipped to here). Lists the caster's `knownSpells`
 * grouped by level with a remaining-slot pip row per level. Tapping a
 * row commits — no confirmation. Auto-cancels after the grace period.
 *
 * Grace period is hardcoded to **4 s** (double the U09 default of 2 s)
 * per the casting design call: spell decisions are higher-stakes than
 * basic attacks and benefit from extra read time.
 *
 * World-freeze: sends `WORLD_FREEZE` on open and `WORLD_UNFREEZE` on
 * close so monsters and ATB pause while the player picks.
 *
 * NOTE: the cast pipeline (S22) and target picker (S23) are not yet
 * wired. For now, tapping a spell row just dismisses the modal.
 */

const GRACE_MS = 4000;

interface OpenState {
  readonly casterId: string;
  readonly hand: "mainHand" | "offHand";
}

export function SpellChooser(): React.ReactElement | null {
  const [open, setOpen] = useState<OpenState | null>(null);
  const [spells, setSpells] = useState<readonly Spell[]>([]);
  const [progress, setProgress] = useState(1);
  const openedAtRef = useRef<number>(0);

  useEffect(() => {
    const off = EventBus.on("ui:open_spell_chooser", ({ casterId, hand }) => {
      setOpen({ casterId, hand });
    });
    return off;
  }, []);

  useEffect(() => {
    if (!open) return;
    const character = PartyStore.getMember(open.casterId);
    if (!character) {
      setOpen(null);
      return;
    }
    gameActor.send({ type: "WORLD_FREEZE" });
    openedAtRef.current = performance.now();
    setProgress(1);

    let cancelled = false;
    const ids = character.knownSpells ?? [];
    void Promise.all(ids.map((id) => loadSpell(id))).then((loaded) => {
      if (cancelled) return;
      setSpells(loaded.filter((s): s is Spell => s !== null));
    });

    const tick = () => {
      const elapsed = performance.now() - openedAtRef.current;
      const remaining = Math.max(0, 1 - elapsed / GRACE_MS);
      setProgress(remaining);
      if (remaining <= 0) close();
      else raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      gameActor.send({ type: "WORLD_UNFREEZE" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.casterId]);

  if (!open) return null;
  const character = PartyStore.getMember(open.casterId);
  if (!character) return null;

  const close = () => {
    setOpen(null);
    setSpells([]);
  };

  // Group by spell level (0 = cantrips).
  const byLevel = new Map<number, Spell[]>();
  for (const sp of spells) {
    const list = byLevel.get(sp.level) ?? [];
    list.push(sp);
    byLevel.set(sp.level, list);
  }
  const levels = [...byLevel.keys()].sort((a, b) => a - b);

  const onPick = (spell: Spell) => {
    const cm = getCombatManager();
    if (!cm) {
      close();
      return;
    }
    // Cantrips burn no slot; everything else needs a level ≥ spell.level.
    const slotLevel =
      spell.level === 0 ? 0 : (findCastableSlot(character, spell.level) ?? spell.level);

    // S23: targets that need a player click (single, aoe, multi) go through
    // the targeting-dispatcher armed state. Self / touch resolve via the
    // CombatManager auto-target path (most-wounded ally / nearest enemy).
    if (spell.targeting === "aoe" || spell.targeting === "single" || spell.targeting === "multi") {
      EventBus.emit("ui:arm_spell", {
        casterId: character.instanceId,
        spellId: spell.id,
        slotLevel,
      });
      close();
      return;
    }

    cm.castSpell({ casterId: character.instanceId, spell, slotLevel });
    close();
  };

  const barRed = progress < 0.25;

  return (
    <div style={overlayStyle} onClick={close}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={progressTrackStyle}>
          <div
            style={{
              ...progressFillStyle,
              transform: `scaleX(${progress})`,
              background: barRed ? "#c14d4d" : "#9bbcd6",
            }}
          />
        </div>
        <div style={headerStyle}>
          <span style={{ fontWeight: 600 }}>{character.name}</span>
          <span style={{ opacity: 0.6, marginLeft: 8 }}>cast a {termSpell()}</span>
        </div>

        {levels.length === 0 ? (
          <div style={emptyStyle}>No {termSpells()} known.</div>
        ) : (
          levels.map((lv) => {
            const remaining = character.spellSlots[lv] ?? 0;
            return (
              <div key={lv} style={levelGroupStyle}>
                <div style={levelHeaderStyle}>
                  <span>{lv === 0 ? "Cantrips" : `Level ${lv}`}</span>
                  {lv > 0 && (
                    <span style={{ opacity: 0.7 }}>{remaining} left</span>
                  )}
                </div>
                {(byLevel.get(lv) ?? []).map((sp) => {
                  const disabled = lv > 0 && remaining === 0;
                  return (
                    <button
                      key={sp.id}
                      onClick={() => !disabled && onPick(sp)}
                      disabled={disabled}
                      style={{
                        ...spellRowStyle,
                        opacity: disabled ? 0.4 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{sp.name}</span>
                      <span style={{ fontSize: 11, opacity: 0.6 }}>{sp.school}</span>
                    </button>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  zIndex: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  // HUD root sets pointer-events: none so the 3D canvas underneath stays
  // clickable; modals must opt back in or taps fall through to nav buttons.
  pointerEvents: "auto",
};

const modalStyle: React.CSSProperties = {
  background: "#1a2030",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  width: "min(420px, 100%)",
  maxHeight: "80vh",
  overflowY: "auto",
  color: "#eee",
  fontSize: 13,
  position: "relative",
};

const progressTrackStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  height: 3,
  background: "rgba(255,255,255,0.08)",
  overflow: "hidden",
};

const progressFillStyle: React.CSSProperties = {
  height: "100%",
  width: "100%",
  transformOrigin: "left",
  transition: "background 120ms linear",
};

const headerStyle: React.CSSProperties = {
  padding: "12px 16px 8px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const emptyStyle: React.CSSProperties = {
  padding: 24,
  textAlign: "center",
  opacity: 0.6,
};

const levelGroupStyle: React.CSSProperties = {
  padding: "8px 0",
};

const levelHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 16px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  opacity: 0.7,
};

const spellRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  padding: "10px 16px",
  background: "transparent",
  border: "none",
  borderTop: "1px solid rgba(255,255,255,0.04)",
  color: "#eee",
  textAlign: "left",
  fontSize: 13,
};
